import { Link, useNavigate } from '@tanstack/react-router';

import { Button } from '@common/ui/buttons/Button';
import { Loader } from '@common/ui/Loader';

import { loadAccountSettings, useAccountStore } from '@entities/account';
import { useIncomingSuperlikes } from '@entities/dating/hooks';
import { useMatchmakerModeStore } from '@entities/matchmaker';

import { analytics, type MatchmakerMode } from '@services/AnalyticsService';

import { ProfilesList } from '@widgets/dating/components';

import { MatchmakerHome } from './components/MatchmakerHome';
import { MatchmakerResult } from './components/MatchmakerResult';
import { ModeSwitch } from './components/ModeSwitch';
import styles from './DatingHome.module.scss';

/** DatingHome's own row above everything else: 8 px, then the 44 px control. */
const TOP_ROW_HEIGHT = 52;

/**
 * The gap between DatingHome's rows: the page's own 12 px in the feed (the
 * feed's card and its info sit 12 px apart, as on today's page), the board's
 * 8 px in matchmaker mode.
 */
const ROW_GAP = { dating: 12, matchmaker: 8 } as const;

/** Everything above the feed's card while the switch shows, in px. */
export const DATING_HOME_TOP_OFFSET = TOP_ROW_HEIGHT + ROW_GAP.dating;

/** Everything above the matchmaker's friend line, in px. */
const MATCHMAKER_TOP_OFFSET = TOP_ROW_HEIGHT + ROW_GAP.matchmaker;

/**
 * The Знакомства tab (plan §4, board 3.1). A user without the matchmaker
 * feature gets today's feed and nothing else; a user in both modes picks one
 * with «Знакомлюсь / Сваха» (remembered on this device); a matchmaker-only
 * user gets matchmaker mode.
 */
export const DatingHome = () => {
  const navigate = useNavigate();
  const status = useAccountStore((state) => state.status);
  const account = useAccountStore((state) => state.account);
  const isAccountLoading = useAccountStore((state) => state.isLoading);
  const storedMode = useMatchmakerModeStore((state) => state.mode);
  const setMode = useMatchmakerModeStore((state) => state.setMode);

  const isMatchmakerOn =
    !!account && account.features.matchmaker && account.matchmaker;
  const hasModeSwitch = isMatchmakerOn && !!account?.dating;
  const mode: MatchmakerMode =
    account && !account.dating
      ? 'matchmaker'
      : hasModeSwitch
        ? storedMode
        : 'dating';

  // Only matchmaker mode needs it here: in the feed, the list reads the same
  // superlikes itself, and a second reader would revalidate them twice.
  const { cards: incomingSuperlikes } = useIncomingSuperlikes({
    isEnabled:
      hasModeSwitch && mode === 'matchmaker' && !!account?.features.superlike,
  });
  const hasIncomingSuperlikes = (incomingSuperlikes?.length ?? 0) > 0;

  if (status === 'unknown' || !account) {
    if (isAccountLoading) return <Loader />;

    return (
      <MatchmakerResult
        testId="dating-home-account-failed"
        icon3d="sad.png"
        title="Ошибка подключения"
        description="Не удалось подключиться к серверу"
        button={
          <Button
            testId="dating-home-retry-button"
            onClick={() => void loadAccountSettings()}
          >
            Повторить
          </Button>
        }
      />
    );
  }

  if (!account.dating && !isMatchmakerOn) {
    return (
      <MatchmakerResult
        testId="dating-home-matchmaker-unavailable"
        icon3d="sad.png"
        title="Режим свахи сейчас недоступен"
        button={
          <Button
            testId="dating-home-enable-dating-button"
            onClick={() => navigate({ to: '/profile/settings' })}
          >
            Включить знакомства
          </Button>
        }
      />
    );
  }

  // Today's feed, exactly as today's page lays it out.
  if (!hasModeSwitch && account.dating) return <ProfilesList />;

  const handleModeChange = (nextMode: MatchmakerMode) => {
    setMode(nextMode);
    analytics.trackMmModeSwitched(nextMode);
  };

  return (
    <div
      className={styles.datingHome}
      data-mode={mode}
      data-test-id="dating-home"
    >
      {hasModeSwitch && (
        <div className={styles.top}>
          <ModeSwitch
            mode={mode}
            hasIncomingSuperlikes={hasIncomingSuperlikes}
            onChange={handleModeChange}
          />
        </div>
      )}

      {!account.dating && (
        <div className={styles.top}>
          <Link
            to="/profile/settings"
            className={styles.alsoDating}
            data-test-id="dating-home-also-dating-link"
          >
            Хочу тоже знакомиться
          </Link>
        </div>
      )}

      {mode === 'dating' ? (
        <ProfilesList topOffset={DATING_HOME_TOP_OFFSET} />
      ) : (
        <MatchmakerHome topOffset={MATCHMAKER_TOP_OFFSET} />
      )}
    </div>
  );
};
